import { Tab } from '@headlessui/react';
import { Account } from '@radixdlt/radix-dapp-toolkit';
import { LoaderFunctionArgs } from 'react-router';
import { Link, Outlet, useFetcher, useLoaderData } from 'react-router';
import BigNumber from 'bignumber.js';
import { useEffect, useState } from 'react';
import AccountSelector from '~/components/account-selector';
import { DataBlock } from "~/components/data-block";
import { socialArrayToObject } from "~/components/socials";
import CGIcon from "~/components/socials/coingecko";
import DiscordIcon from "~/components/socials/discord";
import GithubIcon from "~/components/socials/github";
import TelegramIcon from "~/components/socials/telegram";
import TwitterIcon from "~/components/socials/twitter";
import TransactionButton from "~/components/transaction-button";
import { fetchPrice } from "~/lib/astrolescent";
import { setDbFromContext } from "~/lib/db";
import { generateAddStakeManifest } from "~/lib/manifests";
import { getPairByBaseToken } from "~/lib/models/pair";
import { getTokenStakingByToken } from "~/lib/models/staking";
import { SelectToken, getToken } from "~/lib/models/tokens";
import { ROUND_UP, classNames, getIconUrl, onIconError, prettyValue, shortenAddress } from "~/lib/util";
import { fetchTokenMetaData, fetchTokensDetails, useGatewayApi } from "~/rdt/gateway";
import { useRadix } from "~/rdt/radixProvider";
import { loader as walletLoader } from "~/routes/wallet.$account";

export const sASTRL_RESOURCE_ADDRESS = "resource_rdx1tkm0z9pkz0e59yf2258drzjwaq7axtcf2mupjz2ag9kwww4jvdfd6c";
export const STAKING_POOL_ADDRESS = "pool_rdx1c325zs6dz3un8ykkjavy9fkvvyzarkaehgsl408qup6f95aup3le3w";

export async function loader({ params, context }: LoaderFunctionArgs) {
  setDbFromContext(context);

  if (!params?.address) {
    return ({
      error: "Missing token",
      token: null,
      meta: null,
    });
  }

  const [token] = await getToken(params.address);

  if (!token) {
    return ({
      error: "Missing token",
      token: null,
      meta: null,
    });
  }

  const meta = await fetchTokenMetaData(params.address);

  let locked = {};

  try {
    const radlockResponse = await fetch(`https://radlock-api.fly.dev/token/locked`);
    const radLockedTokens = await radlockResponse.json();

    locked = radLockedTokens[params.address] || {};
  } catch (err) {
    // ignore radlock errors
  }

  let price = await fetchPrice(params.address, new Date());

  console.log(price);

  return ({
    error: null,
    token,
    meta,
    price,
    locked,
  });
}

export default function TokenStaking() {
  const { token, meta, price, locked } = useLoaderData<typeof loader>();
  const [socialUrls, setSocialUrls] = useState(socialArrayToObject(meta["social_urls"]?.value || []));

  return (
    <div className="h-screen space-y-4">
      <p>{meta?.description.value}</p>
      <div className="flex items-center justify-between">
        <div
          className="hover:text-primary-800 dark:text-primary-100 flex cursor-copy items-center space-x-1"
          onClick={() => {
            navigator.clipboard.writeText(token?.address);
          }}
        >
          <span>{shortenAddress(token?.address)}</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
            />
          </svg>
        </div>
        <div className="flex items-center space-x-4">
          {meta?.info_url?.value ? (
            <a href={meta?.info_url?.value} target="_blank" className="hover:text-primary-800 dark:text-primary-100 flex h-6 w-6 items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                />
              </svg>
            </a>
          ) : null}
          {socialUrls["twitter"] ? (
            <a href={socialUrls["twitter"]} target="_blank" className="hover:text-primary-800 dark:text-primary-100 flex h-6 w-6 items-center">
              <TwitterIcon />
            </a>
          ) : null}
          {socialUrls["telegram"] ? (
            <a href={socialUrls["telegram"]} target="_blank" className="hover:text-primary-800 dark:text-primary-100 flex h-6 w-6 items-center">
              <TelegramIcon />
            </a>
          ) : null}
          {socialUrls["discord"] ? (
            <a href={socialUrls["discord"]} target="_blank" className="hover:text-primary-800 dark:text-primary-100 flex h-6 w-6 items-center">
              <DiscordIcon />
            </a>
          ) : null}
          {socialUrls["github"] ? (
            <a href={socialUrls["github"]} target="_blank" className="hover:text-primary-800 dark:text-primary-100 flex h-6 w-6 items-center">
              <GithubIcon />
            </a>
          ) : null}
          {socialUrls["coingecko"] ? (
            <a href={socialUrls["coingecko"]} target="_blank" className="hover:text-primary-800 dark:text-primary-100 flex h-6 w-6 items-center">
              <CGIcon />
            </a>
          ) : null}
        </div>
      </div>
      <dl className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-4">
        <DataBlock title="Price" data={`${prettyValue(price.tokenPriceXRD)} XRD`} />
        <DataBlock title="Circulating Supply" data={`${prettyValue(price.marketCap / price.tokenPriceUSD)} ${token?.symbol}`} />
        <DataBlock title="Locked" data={`${prettyValue(meta.total_supply - (price.marketCap / price.tokenPriceUSD))} ${token?.symbol}`} />
        <DataBlock title="Market Cap" data={`$${prettyValue(price.marketCap)}`} />
      </dl>
    </div>
  );
}
